import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://caoliuhome.com/' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://caoliuhome.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        url: `https://caoliuhome.com/article/${slug}`,
        author: {
          '@type': 'Organization',
          name: '草榴回家的路',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
                  {article.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <span className="text-foreground-900 truncate max-w-[200px]">{article.title}</span>
              </nav>

              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-time-line" />
                    {article.readTime} 分钟阅读
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 leading-tight">
                  {article.title}
                </h1>
              </header>

              <div
                className="prose prose-lg max-w-none mb-12"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="border-t border-background-200 pt-8">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  返回{article.categoryName}列表
                </Link>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-xl font-heading text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <h4 className="text-sm font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}